import { useState, type ReactNode } from "react";
import { ADVANCE_DOCUMENT_ALTERNATIVE } from "@/lib/signalAdvanceEvidence";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import {
  isAdvanceEnrolled,
  setBondAdvanceOptIn,
  type BondAdvanceOptIn,
  type BondFlow,
  type BondSubmission,
} from "@/lib/brokerBonds";
import { messageFor } from "@/lib/errors";

import { buttonClass } from "@/components/buttonStyles";
// BRK-S161 — the offer to enrol in **Signal Advance**.
//
// Signal runs two standing surety programs. **Signal Express** is granted off
// the bureau pulls alone; **Signal Advance** is the one the insured's connected
// bank and accounting feeds support, and it is where a better rate can come
// from. This card is where a broker is asked.
//
// ⚠️ SIGNAL ADVANCE, NEVER "ADVANCED". The earlier drafts of these stories said
// otherwise, and the word is corrected wherever it appears —
// `test/lib/signalAdvanceNaming.test.ts` is what keeps that true of the repo
// rather than of this comment.
//
// ── THE LINKS COME AFTER THE ANSWER, NOT BEFORE ─────────────────────────────
//
// Connecting a bank feed is the insured's consent, and the enrolment is what
// makes discovery mint the links at all. So the offer renders FIRST and the two
// connect panels only once the broker has opted in — handing somebody a bank
// link they never asked for is the shape of a dark pattern, and on a decline
// there is nothing for them to connect.
//
// ── THREE ANSWERS (TWO ON A PRE-APPROVAL), AND `declined` IS NOT SILENCE ────────────────────────────
//
// PMS publishes four states including `null`, and the two absences are
// different facts: `null` is a conversation that has not happened, `declined`
// is one that did. That is why "No thanks" WRITES rather than just closing the
// card — a broker who said no has been asked, and Signal may offer again later
// knowing that.
//
// ── NO OPTIMISTIC SUCCESS ───────────────────────────────────────────────────
//
// The card renders from the ORDER the server answered with, never from a local
// flag (BRK-S113 / BRK-S122). A failed enrolment shows the server's sentence
// and a Retry, and the links stay away — a panel that appeared on a click that
// did not land would be telling the broker their bank feed is on its way.

type AdvanceChoice = {
  value: BondAdvanceOptIn;
  label: string;
  detail: string;
};

/** The three answers on a BOND request, in the scoping document's own order
 *  and words. */
const BOND_CHOICES: readonly AdvanceChoice[] = [
  {
    value: "this_project",
    label: "I want to see if a better rate is available for this project",
    detail:
      "Connect your bank and accounting within 24 hours and we’ll come back within 48.",
  },
  {
    value: "future_projects",
    label:
      "I want to apply for the program for future projects, but I need this bond issued now",
    detail: "The current rate stands for this one. Nothing here holds it up.",
  },
  {
    value: "declined",
    label: "No thanks",
    detail: "We’ll issue on your current program. You can ask us again later.",
  },
];

/**
 * The TWO answers on a standalone PRE-APPROVAL (BRK-S181).
 *
 * A pre-approval has no project and no bond to hold up, so both of the bond
 * request's opt-ins said something false on that screen. The wire vocabulary
 * does not change: the one opt-in is `future_projects` — the PROGRAM-level
 * enrolment, which is what a pre-approval is — and `this_project` is simply
 * never offered on this flow. A flow-neutral value would be a contract change
 * across three repos for no difference in what is recorded.
 */
const PREAPPROVAL_CHOICES: readonly AdvanceChoice[] = [
  {
    value: "future_projects",
    label: "I would like to see if a better rate is available",
    detail:
      "Connect the insured’s bank and accounting and we’ll come back on the rate.",
  },
  {
    value: "declined",
    label: "No thanks",
    detail: "You can ask us again later.",
  },
];

export function SignalAdvanceOffer({
  orderId,
  answer,
  onAnswered,
  links,
  flow = "bond",
}: {
  orderId: string;
  /** The order's flow (BRK-S181): a standalone pre-approval is offered two
   *  answers, a bond request three. */
  flow?: BondFlow;
  /** The order's current answer. `null`/absent offers the program; `declined`
   *  offers it again, because a no is not a permanent no. */
  answer: BondAdvanceOptIn | null | undefined;
  /** The refreshed order the enrolment answered with — the caller owns it, so
   *  the links it now carries render from the server's own body. */
  onAnswered: (order: BondSubmission) => void;
  /** The two connect panels, rendered by the caller and shown only once the
   *  broker has opted in. */
  links: ReactNode;
}) {
  const { authedRequest, identity } = useAuth();
  const [choice, setChoice] = useState<BondAdvanceOptIn | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    if (choice === null) return;
    setBusy(true);
    setError(null);
    try {
      onAnswered(await setBondAdvanceOptIn(authedRequest, orderId, choice));
    } catch (err) {
      // A 409 means somebody already enrolled this order — which is a state, not
      // a failure, but it is also not something this card can resolve on its
      // own, so PMS's own sentence is what shows.
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  // ANSWERED AND ENROLLED: the links, and nothing else. The offer is spent.
  if (isAdvanceEnrolled(answer)) {
    return (
      <div className="flex w-full flex-col gap-4">
        <p role="status" className="text-sm text-green-800 dark:text-green-200">
          ✓ You’re in. We’ll review the connected data and come back on the
          rate.
        </p>
        {links}
      </div>
    );
  }

  return (
    <section
      aria-labelledby="signal-advance"
      className="flex w-full flex-col gap-4 rounded-xl border border-brk-border bg-brk-panel p-4 text-left sm:p-6"
    >
      <div className="flex flex-col gap-1">
        <h2 id="signal-advance" className="text-heading text-brk-text">
          Signal Advance
        </h2>
        <p className="text-sm text-brk-dim">
          {answer === "declined"
            ? "You passed on this earlier — the offer is still open if you’d like a look."
            : "Connect the insured’s bank and accounting and we’ll see whether a better rate is available."}
        </p>
        {/* The document alternative, in one sentence (BRK-S330): each of the
            two links can be replaced by uploaded documents on the account. */}
        <p className="text-sm text-brk-dim">{ADVANCE_DOCUMENT_ALTERNATIVE}</p>
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="sr-only">Signal Advance options</legend>
        {(flow === "preapproval" ? PREAPPROVAL_CHOICES : BOND_CHOICES).map(
          (option) => (
            <label
              key={option.value}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                choice === option.value
                  ? "border-brk-accent bg-brk-accent/5"
                  : "border-brk-border hover:bg-brk-bg"
              }`}
            >
              <input
                type="radio"
                name="signal-advance-choice"
                className="mt-1"
                value={option.value}
                checked={choice === option.value}
                onChange={() => setChoice(option.value)}
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm font-medium text-brk-text">
                  {option.label}
                </span>
                <span className="text-xs text-brk-dim">{option.detail}</span>
              </span>
            </label>
          ),
        )}
      </fieldset>

      {/* WHERE THE LINKS GO, confirmed before they are sent — shown only for
          the two answers that send any.

          It CONFIRMS rather than edits, deliberately: the route's body carries
          the answer and nothing else, so an editable address here would be a
          field with nowhere to travel. The way to change it is the profile,
          which is where the address actually lives. */}
      {choice !== null && choice !== "declined" && identity?.email && (
        <p className="text-sm text-brk-dim">
          We’ll send the connection links to{" "}
          <span className="font-medium text-brk-text">{identity.email}</span>.{" "}
          <Link to="/profile" className="text-brk-accent hover:underline">
            Change your email
          </Link>
        </p>
      )}

      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={() => void confirm()}
        disabled={busy || choice === null}
        className={`${buttonClass()} self-start`}
      >
        {busy ? "Saving…" : error ? "Try again" : "Confirm"}
      </button>
    </section>
  );
}

/** What a DECLINED order says where the links would be — neutral, and never a
 *  nag. The offer above stays available, which is the invitation.
 *
 *  Deliberately NOT the same sentence as the "No thanks" option's own hint: one
 *  describes what picking it would do and the other reports what was picked,
 *  and printing one line twice on one screen reads as a stutter rather than as
 *  a confirmation. */
export function AdvanceDeclinedNotice() {
  return (
    <p role="status" className="text-sm text-brk-dim">
      No problem — nothing about this request changes.
    </p>
  );
}
